import { gql, QueryResult, useMutation } from '@apollo/client'
import * as React from 'react'
import styled, { css } from 'styled-components'
import { ClockListener, clock } from '../../../../common/src/clock'
import Icon from '../../../../common/src/components/Icon'
import { Link } from '../../../../common/src/components/Link'
import Modal from '../../../../common/src/components/Modal'
import TypeaheadBox from '../../../../common/src/components/TypeaheadBox'
import {
  Spacer,
  SmallerText,
  ColoredSpan,
  IntroText,
  BodyTextSpan,
  BodyText,
  InputInline,
  ClickableSpan,
  SmallerTextSpan,
  EllipsisDiv,
} from '../../../../common/src/components/core'
import { Colors, DarkColors, ColorsRGB } from '../../../../common/src/constants'
import { handleError } from '../../../../common/src/data/errors'
import { diffSecs, timeFmt, dateMDTimeFmt } from '../../../../common/src/date'
import { addFeedback } from '../../../../common/src/feedback'
import { getFlag } from '../../../../common/src/flags'
import { formatPhoneNumber } from '../../../../common/src/format'
import { addIfNotPresent } from '../../../../common/src/func'
import memoize from '../../../../common/src/memoize'
import {
  Message,
  FetchThreadQuery,
  MessageTag,
  AnnotateMessageMutation,
  AnnotateMessageMutationVariables,
  FetchMessageDetailsQuery,
  FetchMessageDetailsQueryVariables,
  MessageType,
  MessageUserAnnotationFragment,
  VoiceCallStatus,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { Fragments } from '../../graphql/fragments'
import LazyComponent from '../components/LazyComponent'
import { PollingQuery } from '../components/PollingQuery'
import RichText from '../components/RichText'
import ResendActionModal from './ResendActionModal'

const fetchMessageDetailsQuery = gql`
  query FetchMessageDetails($messageId: Int!) {
    message(messageId: $messageId) {
      ...Message
      messageDetails
      annotations {
        ...MessageUserAnnotation
        brainResultJson
      }
    }
  }
  ${Fragments.message}
  ${Fragments.messageUserAnnotation}
`
const annotateMessageMutation = gql`
  mutation AnnotateMessage($messageId: Int!, $classification: String!, $tags: [MessageTag!]!) {
    annotateMessage(annotation: { messageId: $messageId, classification: $classification, tags: $tags }) {
      id
      classification
      tags
    }
  }
`

const offerShowMsg = 'Reclassifying? Try the Accept Show, Cancel Show or Human Fallback QAs!'

/**
 * For users that whose UI reclassificaiton UI is disabled.
 */
const successorSuggestions: { [key: string]: string } = {
  NONE: 'Reclassifying? Try the Offer Show or Human Fallback QAs!',
  SEND_OFFER_SHOW: offerShowMsg,
  SEND_SHOWING_FULL_RESCHEDULE: offerShowMsg,
  SEND_MULTIPLE_OFFER_SHOW: offerShowMsg,
  SEND_CONFIRM_SHOW: 'Reclassifying? Try the Confirm or Cancel Showing QAs!',
  SEND_RETRY_CONFIRM_SHOW: 'Reclassifying? Try the Confirm or Cancel Showing QAs!',
  SEND_QUALIFY: 'Reclassifying? Try the Qualifications or Human Fallback QAs!',
  SEND_REACTIVATION: 'Reclassifying? Try the Offer Show or Not Interested QAs!',
  SEND_UNIT_INQUIRY: 'Reclassifying? Select the unit type above, or try the Human Fallback QA!',
  SEND_REQUEST_PHOTO_ID: 'Reclassifying? Try the Accept Showing or Human Fallback QAs!',
}

const successorClasses: { [key: string]: string[] } = {
  NONE: ['HUMAN_FALLBACK', 'ASK_FOR_SHOW'],
  SEND_OFFER_SHOW: ['HUMAN_FALLBACK', 'ACCEPT_SHOW'],
  SEND_SHOWING_FULL_RESCHEDULE: ['HUMAN_FALLBACK', 'ACCEPT_SHOW'],
  SEND_MULTIPLE_OFFER_SHOW: ['HUMAN_FALLBACK', 'ACCEPT_SHOW'],
  SEND_CONFIRM_SHOW: ['HUMAN_FALLBACK', 'CONFIRM_SHOW', 'CANCEL_SHOW'],
  SEND_RETRY_CONFIRM_SHOW: ['HUMAN_FALLBACK', 'CONFIRM_SHOW', 'CANCEL_SHOW'],
  SEND_UNIT_INQUIRY: ['HUMAN_FALLBACK', 'RESPOND_WITH_UNIT_TYPE'],
  SEND_REQUEST_PHOTO_ID: ['HUMAN_FALLBACK', 'RECEIVE_PHOTO_ID'],
  SEND_QUALIFY: [],
  SEND_REACTIVATION: [],
}

export default class MessageItem extends React.PureComponent<{
  message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  thread: FetchThreadQuery['thread']
  isSelected: boolean
  shouldScrollIntoView: boolean
  index: number
  messageAction?: FetchThreadQuery['thread']['recentActions'][0]
  disableReclassification?: boolean
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  onClickOuter: (e: React.MouseEvent<HTMLElement>) => void
  onReplyTo: (message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]) => void
  messageLinks: FetchThreadQuery['thread']['messagesWithLinks']['messageLinks']
  user?: FetchUserContextQuery['self']
}> {
  render() {
    const { message, thread, isSelected, onClick, onClickOuter, onReplyTo, messageLinks, user, messageAction } =
      this.props

    const showAnnotator = isSelected && message.isInbound

    const msgAddrUs = message.isInbound ? message.addressTo : message.addressFrom
    const msgAddrThem = message.isInbound ? message.addressFrom : message.addressTo
    const showAddrs = msgAddrUs !== thread.addressUs || msgAddrThem !== thread.addressThem

    return (
      <MessageWrapper key={message.id}>
        {showAnnotator && this.renderAnnotator()}
        <MessageUnit
          key={message.id}
          message={message}
          showAddrs={showAddrs}
          isSelected={isSelected}
          onClick={onClick}
          onClickOuter={onClickOuter}
          timestampRef={showAnnotator ? undefined : this.scrollToRefBound}
          thread={thread}
          onReplyTo={onReplyTo}
          messageLinks={messageLinks}
          user={user}
          messageAction={messageAction}
        />
      </MessageWrapper>
    )
  }

  private renderAnnotator() {
    const { message, thread, index, onClickOuter } = this.props

    const prevClassifiedOutbounds = thread.messagesWithLinks.messages
      .slice(0, index)
      .filter(msg => !msg.isInbound && msg.classification != null)
    const lastMsg = prevClassifiedOutbounds[prevClassifiedOutbounds.length - 1]
    const lastClassification = lastMsg ? lastMsg.classification : 'NONE'
    let allowedClasses = [] as string[]
    if (lastClassification != null) {
      allowedClasses = successorClasses[lastClassification] || []
    }
    if (
      thread.messagesWithLinks.messages.some(m => m.classification === 'SEND_REQUEST_PHOTO_ID') &&
      message.mediaUrl != null
    ) {
      addIfNotPresent(allowedClasses, 'HUMAN_FALLBACK')
      addIfNotPresent(allowedClasses, 'RECEIVE_PHOTO_ID')
    }

    const reclassificationCallToAction = lastClassification ? successorSuggestions[lastClassification] : undefined

    return (
      <MessageAnnotator
        innerRef={this.scrollToRefBound}
        onClickOuter={onClickOuter}
        message={message}
        allowedClasses={allowedClasses}
        disableReclassification={this.props.disableReclassification}
        reclassificationCallToAction={reclassificationCallToAction}
        currentClass={message.classification}
      />
    )
  }

  private scrollToRefBound = (e: HTMLElement) => {
    const { shouldScrollIntoView } = this.props
    if (e != null && shouldScrollIntoView) {
      e.scrollIntoView()
    }
  }
}

interface MessageAnnotatorProps {
  message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  allowedClasses: string[]
  disableReclassification?: boolean
  reclassificationCallToAction?: string
  currentClass?: string | null
  innerRef: (e: HTMLDivElement) => void
  onClickOuter: (e: React.MouseEvent<HTMLElement>) => void
}

class MessageAnnotatorImpl extends React.PureComponent<MessageAnnotatorProps & { mutate: any }> {
  private clickHandlers: { [key: string]: () => void } = {}

  render() {
    const { allowedClasses, disableReclassification, reclassificationCallToAction, message, innerRef, onClickOuter } =
      this.props

    return (
      <MessageAnnotatorRoot onClick={onClickOuter} ref={innerRef}>
        {disableReclassification ? (
          <MessageClassChooserElem key={message.classification || 'NONE'} isSelected={false}>
            {reclassificationCallToAction}
          </MessageClassChooserElem>
        ) : (
          allowedClasses.map(c => (
            <MessageClassChooserElem
              key={c}
              isSelected={c === message.classification}
              onClick={memoize(this.clickHandlers, c, () => () => this.onChangeClass(c))}
            >
              {c}
            </MessageClassChooserElem>
          ))
        )}
        <Spacer height={8} />
        <MessageTagChooser tags={message.tags} onAdd={this.onAddTagBound} onRemove={this.onRemoveTagBound} />
        <Spacer height={8} />
      </MessageAnnotatorRoot>
    )
  }

  private onAddTagBound = (tag: MessageTag) => {
    const newTags = this.props.message.tags.slice()
    newTags.push(tag)
    this.setTags(newTags)
  }

  private onRemoveTagBound = (tag: MessageTag) => {
    const newTags = this.props.message.tags.slice()
    const ix = newTags.indexOf(tag)
    if (ix < 0) {
      return
    }
    newTags.splice(ix, 1)
    this.setTags(newTags)
  }

  private setTags(newTags: MessageTag[]) {
    const { classification } = this.props.message

    if (classification == null) {
      window.alert('Cannot tag unclassified message')
      return
    }

    this.annotate(classification, newTags)
  }

  private onChangeClass(classification: string) {
    this.annotate(classification, this.props.message.tags)
  }

  private annotate(classification: string, tags: MessageTag[]) {
    const { id } = this.props.message
    this.props
      .mutate({
        variables: {
          messageId: id,
          classification,
          tags,
        },
      })
      .then(() => addFeedback('Saved message'))
      .catch(handleError)
  }
}

interface MessageTagChooserProps {
  tags: MessageTag[]
  onAdd: (tag: MessageTag) => void
  onRemove: (tag: MessageTag) => void
}

function prettyTag(t: MessageTag) {
  if (t.startsWith('Q_')) {
    return tagToLower(t.substring('Q_'.length) + '?')
  } else if (t.startsWith('UNIT_')) {
    return t.substring('UNIT_'.length)
  } else if (t.startsWith('LANGUAGE_')) {
    if (t === MessageTag.LANGUAGE_OTHER) {
      return 'Other Language'
    }

    return tagToLower(t.substring('LANGUAGE_'.length))
  }
  return tagToLower(t)
}

function tagToLower(t: string) {
  t = t.replace(/_/g, ' ')
  return t.substring(0, 1) + t.substring(1).toLowerCase()
}

const deprecatedTags = new Set([
  MessageTag.LANGUAGE_CHINESE,
  MessageTag.LANGUAGE_ENGLISH,
  MessageTag.LANGUAGE_FRENCH,
  MessageTag.LANGUAGE_GERMAN,
  MessageTag.LANGUAGE_ITALIAN,
  MessageTag.LANGUAGE_PORTUGUESE,
  MessageTag.LANGUAGE_RUSSIAN,
  MessageTag.LANGUAGE_SPANISH,
  MessageTag.LANGUAGE_OTHER,
])

class MessageTagChooser extends React.PureComponent<MessageTagChooserProps, { showAll: boolean }> {
  private clickHandlers = {}
  state = { showAll: false }

  render() {
    if (this.state.showAll) {
      return <MessageTagsModal onClose={this.onCloseModal} onSelect={this.onChooseTag} />
    }

    const { tags, onRemove } = this.props
    // remove deprecated tags
    const filteredTags = tags.filter(t => !deprecatedTags.has(t))
    const pellets = filteredTags.map(t => (
      <MessageTagPellet key={t} tag={t} onRemove={memoize(this.clickHandlers, t, () => () => onRemove(t))} />
    ))
    return (
      <div>
        <MessageTagPelletsWrap>{pellets}</MessageTagPelletsWrap>
        <TypeaheadBox<string>
          key="input"
          loadFunc={this.loadTags}
          rowFunc={this.renderTagOption}
          onChoose={this.onChooseTag}
          placeholder={'Add tag...'}
          inputComponent={TagChooserInput}
          backgroundZIndex={3}
          showResultsWhenEmpty
          fadeBackground
        />
      </div>
    )
  }

  private loadTags = (prefix: string): Promise<string[]> => {
    prefix = prefix.toUpperCase()
    const { tags } = this.props
    const ret = Object.keys(MessageTag).filter(
      t => !tags.includes(t as MessageTag) && t.includes(prefix) && !deprecatedTags.has(t as MessageTag)
    )
    ret.length = Math.min(ret.length, 6)
    ret.push('Show all')
    return Promise.resolve(ret)
  }

  private renderTagOption = (t: string, prefix: string) => {
    const style = { marginBottom: '-3px' }
    if (t === 'Show all') {
      return <SmallerText style={style}>{t}</SmallerText>
    }

    prefix = prefix.toUpperCase()
    const disp = prettyTag(t as MessageTag)
    const ix = disp.toLowerCase().indexOf(prefix.toLowerCase())
    const before = disp.substring(0, ix)
    const during = disp.substring(ix, ix + prefix.length)
    const after = disp.substring(ix + prefix.length)
    return (
      <SmallerText style={style}>
        {before}
        <ColoredSpan color="white">{during}</ColoredSpan>
        {after}
      </SmallerText>
    )
  }

  private onChooseTag = (tag: string) => {
    if (tag === 'Show all') {
      this.setState({ showAll: true })
    } else {
      this.setState({ showAll: false })
      this.props.onAdd(tag as MessageTag)
    }
  }

  private onCloseModal = () => {
    this.setState({ showAll: false })
  }
}

class MessageTagsModal extends React.PureComponent<{
  onSelect: (t: MessageTag) => void
  onClose: () => void
}> {
  private handlers = {}

  render() {
    const { onClose, onSelect } = this.props

    const tAll = Object.keys(MessageTag) as MessageTag[]
    const tQ = tAll.filter(t => t.startsWith('Q_'))
    const tU = tAll.filter(t => t.startsWith('UNIT_'))
    const tL = tAll.filter(t => t.startsWith('LANGUAGE_'))
    const tO = tAll.filter(t => !t.startsWith('Q_') && !t.startsWith('UNIT_') && !t.startsWith('LANGUAGE_'))

    const renderMessageTag = (t: MessageTag) => (
      <MTag key={t} onClick={memoize(this.handlers, t, () => () => onSelect(t))}>
        {prettyTag(t)}
      </MTag>
    )

    return (
      <Modal title="All message tags" width={960} onClose={onClose}>
        <IntroText color="white">
          Questions
          <Spacer width={16} />
          <BodyTextSpan>Messages asking one or more questions</BodyTextSpan>
        </IntroText>
        <Spacer height={16} />
        <MTagChooser>{tQ.map(renderMessageTag)}</MTagChooser>
        <Spacer height={32} />
        <IntroText color="white">
          Unit types
          <Spacer width={16} />
          <BodyTextSpan>Messages that mention one or more unit types</BodyTextSpan>
        </IntroText>
        <Spacer height={16} />
        <MTagChooser>{tU.map(renderMessageTag)}</MTagChooser>
        <Spacer height={32} />
        <IntroText color="white">
          Languages
          <Spacer width={16} />
          <BodyTextSpan>Inbounds written in different languages</BodyTextSpan>
        </IntroText>
        <Spacer height={16} />
        <MTagChooser>{tL.map(renderMessageTag)}</MTagChooser>
        <Spacer height={32} />
        <IntroText color="white">
          Other tags
          <Spacer width={16} />
          <BodyTextSpan>For example, use "Feedback for site" to tag feedback for onsite staff</BodyTextSpan>
        </IntroText>
        <Spacer height={16} />
        <MTagChooser>{tO.map(renderMessageTag)}</MTagChooser>
      </Modal>
    )
  }
}

const MTag = styled(BodyText).attrs({ color: 'silver' })`
  display: inline-block;
  padding: 8px 12px;
  border-radius: 2px;
  cursor: pointer;

  &:hover {
    color: ${Colors.white};
    background-color: ${Colors.charcoal};
  }
`

const MTagChooser = styled.div`
  margin: -8px -12px;
`

const TagChooserInput = styled(InputInline)`
  padding: 8px 0 1px 0;
  width: 192px;
`

const MessageTagPelletsWrap = styled.div`
  margin-left: -8px;
`

function MessageTagPellet({ tag, onRemove }: { tag: MessageTag; onRemove: () => void }) {
  return (
    <MessageTagWrap>
      <ClickableSpan>{prettyTag(tag)}</ClickableSpan>
      <Link color="silver" onClick={onRemove}>
        &nbsp;&nbsp;✕&nbsp;&nbsp;
      </Link>
    </MessageTagWrap>
  )
}

const MessageTagWrap = styled(SmallerText)`
  display: inline-block;
  margin-right: 4px;
  padding: 2px 4px 2px 8px;
  border-radius: 2px;
  &:hover {
    background: ${Colors.black};
  }
`

const MessageAnnotatorRoot = styled.div`
  padding: 0;
  margin-bottom: 16px;
  width: 360px;
`

function MessageAnnotator(props: MessageAnnotatorProps) {
  const [mutate] = useMutation<AnnotateMessageMutation, AnnotateMessageMutationVariables>(annotateMessageMutation)
  return <MessageAnnotatorImpl {...props} mutate={mutate} />
}

interface MessageClassDetailsProps {
  currentClass?: string | null
  message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  thread: FetchThreadQuery['thread']
  onReplyTo: (message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]) => void
}

type MessageClassDetailsImplProps = MessageClassDetailsProps &
  QueryResult<FetchMessageDetailsQuery, FetchMessageDetailsQueryVariables>

// Show the user how a given message was classified.
// Let the user expand and collapse detailed debugging info.
class MessageClassDetailsImpl extends React.PureComponent<MessageClassDetailsImplProps, { isExpanded: boolean }> {
  state = { isExpanded: false }

  UNSAFE_componentWillReceiveProps(nextProps: MessageClassDetailsImplProps) {
    if (nextProps.currentClass !== this.props.currentClass && nextProps.refetch) {
      nextProps.refetch().catch(handleError)
    }
  }

  onReplyToClicked = () => {
    this.props.onReplyTo(this.props.message)
  }

  render() {
    const { data, thread } = this.props
    if (data == null) {
      return null
    }

    const { isExpanded } = this.state
    const { message } = data

    return (
      <MessageClassDetailsRoot>
        {isExpanded && this.renderMessageDetails()}
        {this.renderControls(message, isExpanded, thread)}
      </MessageClassDetailsRoot>
    )
  }

  private renderControls(
    message: FetchMessageDetailsQuery['message'],
    isExpanded: boolean,
    thread: FetchThreadQuery['thread']
  ) {
    const links = []
    links.push(
      <Link key="toggle" onClick={this.toggleExpandedBound} color="silver">
        {isExpanded ? 'Collapse' : 'Details'}
      </Link>
    )
    if (message.messageType === 'EMAIL') {
      links.push(
        <span key="sep1">
          {links.length > 0 && ' '}
          Email{' '}
        </span>
      )
      links.push(this.renderMessageLink(message.id, 'json'))
      links.push(<span key="sep2">{', '}</span>)
      links.push(this.renderMessageLink(message.id, 'text'))
      links.push(<span key="sep3">{', '}</span>)
      links.push(this.renderMessageLink(message.id, 'html'))
    }
    if (message.externalUrl != null) {
      if (links.length > 0) {
        links.push(<span key="sep4">{', '}</span>)
      }
      links.push(
        <Link key="source" color="snow" href={message.externalUrl}>
          source
        </Link>
      )
    }
    if (message.actionUrl != null) {
      if (links.length > 0) {
        links.push(<span key="sep5">{', '}</span>)
      }
      links.push(
        <Link key="actionLink" color="snow" href={message.actionUrl}>
          action
        </Link>
      )
    }
    if (
      message.isInbound &&
      message.messageType === MessageType.EMAIL &&
      message.addressFrom === thread.addressThem // prevents replying to ILS inbound emails
    ) {
      links.push(
        <ReplyToContainer key="reply">
          <Spacer width={16} />
          <Link color="silver" onClick={this.onReplyToClicked}>
            <Icon name="message-reply" color="silver" />
            <Spacer width={8} />
            Reply
          </Link>
        </ReplyToContainer>
      )
    }
    if (message.isInbound) {
      links.push(
        <Link key="galaxy" color="silver" href={'/leasing/data/galaxy?id=' + message.id}>
          {'.'}
        </Link>
      )
      links.push(
        <Link key="showing-negotiator" color="silver" href={'/leasing/data/showing_negotiator?id=' + message.id}>
          {'.'}
        </Link>
      )
      links.push(
        <Link key="schema-driven-model" color="silver" href={'/leasing/data/schema_driven_model?id=' + message.id}>
          {'.'}
        </Link>
      )
      links.push(
        <Link key="ils-parser" color="silver" href={'/leasing/data/ils_parser?id=' + message.id}>
          {'.'}
        </Link>
      )
      links.push(
        <Link key="name-parser" color="silver" href={'/leasing/data/name_parser?id=' + message.id}>
          {'.'}
        </Link>
      )
      links.push(
        <Link key="ils-classifier" color="silver" href={'/leasing/data/ils_classifier?id=' + message.id}>
          {'.'}
        </Link>
      )
    }
    return <BodyText style={{ textAlign: message.isInbound ? 'left' : 'right' }}>{links}</BodyText>
  }

  private renderMessageDetails() {
    const { data } = this.props
    if (!data || !data.message) {
      return null
    }
    const { messageDetails, annotations } = data.message
    return (
      <div>
        <MessageClassDetailsElem>
          <SmallerText color="white">Message Details</SmallerText>
          <Spacer height={8} />
          <MessageClassPre>{messageDetails}</MessageClassPre>
        </MessageClassDetailsElem>
        {annotations &&
          annotations.map(annot => (
            <MessageClassDetailsElem key={annot.user ? annot.user.id : 0}>
              <SmallerText color="white">{this.renderAnnotSummary(annot)}</SmallerText>
              {annot.brainResultJson && <Spacer height={8} />}
              <MessageClassPre>{annot.brainResultJson}</MessageClassPre>
            </MessageClassDetailsElem>
          ))}
      </div>
    )
  }

  private renderMessageLink(id: number, format: string) {
    return (
      <Link key={format} opensNewTab={true} color="snow" href={'/leasing/data/email?type=' + format + '&id=' + id}>
        {format}
      </Link>
    )
  }

  private toggleExpandedBound = () => {
    this.setState({ isExpanded: !this.state.isExpanded })
  }

  private renderAnnotSummary(annot: MessageUserAnnotationFragment) {
    const name = annot.user ? annot.user.name : 'Brain'
    const tags = annot.tags == null ? 'Untagged' : annot.tags.length === 0 ? 'No tags' : annot.tags.join(', ')
    const classification = annot.classification || 'Unclassified'
    return [name, classification, tags].join(' · ')
  }
}

const MessageClassDetailsRoot = styled.div`
  margin-top: 8px;
  padding: 0 1px;
  min-height: 15px;
  width: 100%;
`

const MessageClassDetailsElem = styled.div`
  margin: 8px 0 8px 0;
  padding: 16px 16px 12px 16px;
  border-radius: 4px;
  background-color: ${Colors.charcoal};
`

const MessageClassPre = styled.pre`
  font: 10px monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: ${Colors.midway};
`

function MessageClassDetails(props: MessageClassDetailsProps) {
  return (
    <PollingQuery
      query={fetchMessageDetailsQuery}
      variables={{ messageId: props.message.id }}
      fetchPolicy="cache-and-network"
    >
      {(res: QueryResult<FetchMessageDetailsQuery, FetchMessageDetailsQueryVariables>) => (
        <MessageClassDetailsImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}

const MessageClassChooserElem = styled(SmallerText)`
  ${({ isSelected }: { isSelected: boolean }) => css`
    display: inline-block;
    margin-right: 8px;
    cursor: pointer;
    color: ${isSelected ? Colors.sky : Colors.midway};
    &:hover {
      color: ${isSelected ? Colors.sky : Colors.white};
    }
  `};
`

interface MessageEmailSubjectProps {
  message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  showTranslatedEmailSubjectIfExists: boolean
  defaultEmailSubject?: string
}

const MessageEmailSubject = (props: MessageEmailSubjectProps) => {
  const { message, showTranslatedEmailSubjectIfExists, defaultEmailSubject } = props

  if (!message.translatedEmailSubject) {
    return <div>{message.emailSubject || defaultEmailSubject}</div>
  }

  const englishEmailSubject = message.isInbound ? message.translatedEmailSubject : message.emailSubject
  const nonEnglishEmailSubject = message.isInbound ? message.emailSubject : message.translatedEmailSubject

  return (
    <>
      <div>{englishEmailSubject || defaultEmailSubject}</div>
      {showTranslatedEmailSubjectIfExists && nonEnglishEmailSubject && (
        <>
          <Spacer height={4} />
          <BodyText>{nonEnglishEmailSubject}</BodyText>
        </>
      )}
    </>
  )
}

interface MessageUnitProps {
  message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]
  messageAction?: FetchThreadQuery['thread']['recentActions'][0]
  isSelected: boolean
  showAddrs: boolean
  onClick: (e: React.MouseEvent<HTMLElement>) => void
  onClickOuter: (e: React.MouseEvent<HTMLElement>) => void
  timestampRef?: (e: HTMLDivElement) => void
  thread: FetchThreadQuery['thread']
  onReplyTo: (message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0]) => void
  messageLinks: FetchThreadQuery['thread']['messagesWithLinks']['messageLinks']
  user?: FetchUserContextQuery['self']
}

interface MessageUnitState {
  expandable?: boolean
  resending?: boolean
}

class MessageUnit extends React.PureComponent<MessageUnitProps, MessageUnitState> {
  private bodyRef: React.RefObject<HTMLDivElement>

  constructor(props: MessageUnitProps) {
    super(props)
    this.state = {}
    this.bodyRef = React.createRef<HTMLDivElement>()
  }

  componentDidMount() {
    this.maybeCollapse()
  }

  componentDidUpdate() {
    this.maybeCollapse()
  }

  render() {
    const { expandable, resending } = this.state
    const { message, messageAction, isSelected, onClick, onClickOuter, timestampRef, onReplyTo, thread, user } =
      this.props

    const alignTranslationWarning = message.isInbound ? 'left' : 'right'
    const isTranslated = message.censoredTranslatedShortBody || message.translatedEmailSubject

    return (
      <MessageElem isInbound={message.isInbound}>
        <WidthLimit onClick={onClickOuter}>
          <MessageBubble
            isSelected={isSelected}
            isInbound={message.isInbound}
            hasMedia={!!message.mediaUrl}
            onClick={onClick}
          >
            <MessageTimestamp isInbound={message.isInbound} highlight={isSelected} ref={timestampRef}>
              {this.renderIndicator()} {this.renderAuthorAndClass()} {this.renderTime()}
            </MessageTimestamp>
            {this.renderHeader()}
            <MessageBody isSelected={isSelected} ref={this.bodyRef}>
              {this.renderBody()}
            </MessageBody>
            {message.mediaUrl && (
              <MessageImageContainer>
                <MessageImage src={message.mediaUrl} alt="[Image From Prospect]" />
              </MessageImageContainer>
            )}
            {expandable && !isSelected && <MsgExpandButton />}
          </MessageBubble>
          {!isSelected && isTranslated && (
            <>
              <Spacer height={4} />
              <SmallerText color="midway" align={alignTranslationWarning}>
                translated by DeepL
              </SmallerText>
            </>
          )}
          {isSelected && (
            <MessageClassDetails
              thread={thread}
              onReplyTo={onReplyTo}
              message={message}
              currentClass={message.classification}
            />
          )}
        </WidthLimit>
        {user && resending && messageAction && (
          <ResendActionModal
            onClose={this.onModalClose}
            validationErrors={messageAction.validationErrors}
            message={message}
            user={user}
            thread={thread}
          />
        )}
      </MessageElem>
    )
  }

  private onModalClose = () => this.setState({ resending: false })

  private warning = (message: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0], symbol: string) => (
    <WarningSpan title={'Failed to send message: ' + (message.errorMessage != null ? message.errorMessage : '')}>
      {symbol}
    </WarningSpan>
  )

  private renderIndicator() {
    const { message, messageAction } = this.props
    if (message.messageStatus === 'SENDING') {
      return '...'
    } else if (message.messageStatus === 'SEND_FAILED') {
      if (messageAction && messageAction.validationErrors.length > 0) {
        return <Link onClick={this.onWarningClick}>{this.warning(message, '⚠')}</Link>
      } else {
        return this.warning(message, '⚠')
      }
    } else {
      return null
    }
  }

  private onWarningClick = () => this.setState({ resending: true })

  private renderAuthorAndClass() {
    const { message } = this.props
    const showClass = message.classification !== 'SEND_HUMAN'
    const numTags = message.tags.length
    const isExperiment = message.outboundTags.some(x => x.endsWith('_TREAT'))

    return (
      <SmallerTextSpan color="sky">
        {isExperiment && <Icon name="message-experiment" color="mint" />}
        <Spacer width={4} />
        {/* if message is outbound and it's fully automated and it's experimental, then display icon */}
        {message.author && (getFlag('hideOperatorNames') ? `Operator ${message.author.id}` : message.author.name)}
        {message.author && showClass && ' '}
        {showClass && message.classification}
        {numTags > 0 && ' +' + numTags}
      </SmallerTextSpan>
    )
  }

  private renderTime() {
    const { message } = this.props
    const sentSecsAgo = diffSecs(new Date(), message.timeCreated)
    const timeStr = sentSecsAgo < 60 * 60 * 8 ? timeFmt(message.timeCreated) : dateMDTimeFmt(message.timeCreated)
    return <SmallerTextSpan>{timeStr}</SmallerTextSpan>
  }

  private renderHeader() {
    const { message, showAddrs, isSelected } = this.props
    const lines = []
    if (message.messageType === 'EMAIL' && message.censoredShortBody === '') {
      return null
    }

    if (showAddrs) {
      lines.push(<MsgAttrLine key="from" label="From" value={message.addressFrom} />)
      lines.push(<MsgAttrLine key="to" label="To" value={message.addressTo} />)
    }
    if (message.leasingAttributionSource) {
      lines.push(<MsgAttrLine key="source" label="Source" value={message.leasingAttributionSource} />)
    }

    if (message.messageType === 'EMAIL') {
      lines.push(
        <MessageEmailSubject
          message={message}
          showTranslatedEmailSubjectIfExists={isSelected}
          defaultEmailSubject="(no subject)"
        />
      )
    } else if (message.messageType === 'CALENDAR') {
      let subjElem = (
        <span key="cal">
          <CharLogo>☑</CharLogo>{' '}
          <MessageEmailSubject
            message={message}
            showTranslatedEmailSubjectIfExists={isSelected}
            defaultEmailSubject="Calendar Event"
          />
        </span>
      )
      if (message.calEventId != null) {
        const eventUrl = '/leasing/data/cal?id=' + encodeURIComponent(message.calEventId)
        subjElem = (
          <Link href={eventUrl} key="cal">
            {subjElem}
          </Link>
        )
      }
      lines.push(subjElem)
    }

    if (lines.length === 0) {
      return null
    } else {
      return <MessageSubject>{lines}</MessageSubject>
    }
  }

  /**
   * Renders a single message item with text. Some messages are just voice calls,
   * and some are email reports to agents, which we show as a thumbnailed preview.
   */
  private renderBody() {
    const { message, messageLinks } = this.props

    if (message.messageType === 'VOICE') {
      return <Call msg={message} />
    } else if (
      // show iframe thumbnail for reporting emails
      message.messageType === 'EMAIL' &&
      !message.isInbound &&
      (message.classification === 'SEND_AUTO_MORNING_UPDATE' ||
        message.classification === 'SEND_AUTO_EVENING_FEEDBACK' ||
        message.classification === 'SEND_AUTO_WEEKLY_FUNNEL' ||
        message.classification === 'SEND_AUTO_NPS_SURVEY' ||
        message.classification === 'SEND_API_DOCS')
    ) {
      return (
        <ThumbnailWrapper>
          <ThumbnailComponent src={getThumbnailLink(message.id)}></ThumbnailComponent>
        </ThumbnailWrapper>
      )
    } else if (message.censoredShortBody === '' && message.mediaUrl == null) {
      return '(empty message)'
    } else if (message.censoredTranslatedShortBody && message.isInbound) {
      return this.renderBodyAndTranslatedBody(message.censoredTranslatedShortBody, message.censoredShortBody)
    } else if (message.censoredTranslatedShortBody && !message.isInbound) {
      return this.renderBodyAndTranslatedBody(message.censoredShortBody, message.censoredTranslatedShortBody)
    } else {
      return <RichText messageLinks={messageLinks}>{message.censoredShortBody}</RichText>
    }
  }

  private renderBodyAndTranslatedBody(body: string, translatedBody: string) {
    const { messageLinks, isSelected } = this.props
    return (
      <>
        <RichText messageLinks={messageLinks}>{body}</RichText>
        {isSelected && (
          <>
            <Spacer height={4} />
            <BodyText>{translatedBody}</BodyText>
          </>
        )}
      </>
    )
  }
  private maybeCollapse() {
    const { expandable } = this.state
    if (expandable !== undefined) {
      return
    }
    const { clientHeight, scrollHeight } = this.bodyRef.current!
    if (clientHeight < scrollHeight) {
      /** we've hit the CSS max-height limit, show an Expand button */
      this.setState({ expandable: true })
    }
  }
}

export function MsgAttrLine({
  label,
  value,
  onValueClick,
}: {
  label: string
  value: string
  onValueClick?: () => void
}) {
  const text = formatPhoneNumber(value) // format, if it's a phone #

  const valueElement = onValueClick ? (
    <Link color="silver" onClick={onValueClick}>
      {text}
    </Link>
  ) : (
    <ColoredSpan color="silver">{text}</ColoredSpan>
  )

  return (
    <BodyText>
      <EllipsisDiv>
        {label}
        <Spacer width={16} />
        {valueElement}
      </EllipsisDiv>
    </BodyText>
  )
}

function getThumbnailLink(messageId: number): string {
  return '/leasing/data/email?type=html&id=' + messageId
}

const ThumbnailWrapper = styled.div`
  width: 600px;
  height: 390px;
  background: #f9f9f9;

  /**
   * Adds a placeholder image for the thumbnail before the iframe is loaded.
   */
  &::before {
    position: absolute;
    left: 44%;
    top: 40%;
    opacity: 0.2;
    display: block;
    zoom: 1.4;
    transform: scale(1.4);
    content: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiA/PjwhRE9DVFlQRSBzdmcgIFBVQkxJQyAnLS8vVzNDLy9EVEQgU1ZHIDEuMS8vRU4nICAnaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkJz48c3ZnIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDMyIDMyIiBoZWlnaHQ9IjMycHgiIGlkPSJMYXllcl8xIiB2ZXJzaW9uPSIxLjEiIHZpZXdCb3g9IjAgMCAzMiAzMiIgd2lkdGg9IjMycHgiIHhtbDpzcGFjZT0icHJlc2VydmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPjxnIGlkPSJwaG90b18xXyI+PHBhdGggZD0iTTI3LDBINUMyLjc5MSwwLDEsMS43OTEsMSw0djI0YzAsMi4yMDksMS43OTEsNCw0LDRoMjJjMi4yMDksMCw0LTEuNzkxLDQtNFY0QzMxLDEuNzkxLDI5LjIwOSwwLDI3LDB6ICAgIE0yOSwyOGMwLDEuMTAyLTAuODk4LDItMiwySDVjLTEuMTAzLDAtMi0wLjg5OC0yLTJWNGMwLTEuMTAzLDAuODk3LTIsMi0yaDIyYzEuMTAyLDAsMiwwLjg5NywyLDJWMjh6IiBmaWxsPSIjMzMzMzMzIi8+PHBhdGggZD0iTTI2LDRINkM1LjQ0Nyw0LDUsNC40NDcsNSw1djE4YzAsMC41NTMsMC40NDcsMSwxLDFoMjBjMC41NTMsMCwxLTAuNDQ3LDEtMVY1QzI3LDQuNDQ3LDI2LjU1Myw0LDI2LDR6ICAgIE0yNiw1djEzLjg2OWwtMy4yNS0zLjUzQzIyLjU1OSwxNS4xMjMsMjIuMjg3LDE1LDIyLDE1cy0wLjU2MSwwLjEyMy0wLjc1LDAuMzM5bC0yLjYwNCwyLjk1bC03Ljg5Ni04Ljk1ICAgQzEwLjU2LDkuMTIzLDEwLjI4Nyw5LDEwLDlTOS40NCw5LjEyMyw5LjI1LDkuMzM5TDYsMTMuMDg3VjVIMjZ6IE02LDE0LjZsNC00LjZsOC4wNjYsOS4xNDNsMC41OCwwLjY1OEwyMS40MDgsMjNINlYxNC42eiAgICBNMjIuNzQsMjNsLTMuNDI4LTMuOTU1TDIyLDE2bDQsNC4zNzlWMjNIMjIuNzR6IiBmaWxsPSIjMzMzMzMzIi8+PHBhdGggZD0iTTIwLDEzYzEuNjU2LDAsMy0xLjM0MywzLTNzLTEuMzQ0LTMtMy0zYy0xLjY1OCwwLTMsMS4zNDMtMywzUzE4LjM0MiwxMywyMCwxM3ogTTIwLDhjMS4xMDIsMCwyLDAuODk3LDIsMiAgIHMtMC44OTgsMi0yLDJjLTEuMTA0LDAtMi0wLjg5Ny0yLTJTMTguODk2LDgsMjAsOHoiIGZpbGw9IiMzMzMzMzMiLz48L2c+PC9zdmc+');
  }

  /**
   * makes the iframe not interactive (no scroll inside scroll)
   */
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`

/**
 * Adds a "thumbnail" image using a non-interactive iframe. Pass a dynasty.com resource as source.
 *
 * This component will fade-in (showing a thumbnail SVG preview first) after the iframe loads the page.
 */
class ThumbnailComponent extends React.PureComponent<{ src: string }, { loaded: boolean }> {
  state = {
    loaded: false,
  }

  render() {
    const { loaded } = this.state
    return (
      <LazyComponent
        componentGenerator={loaded ? this.renderLoadedThumbnail : this.renderUnloadedThumbnail}
        onVisibilityChange={this.handleVisibilityChange}
      />
    )
  }

  private renderLoadedThumbnail = () => this.renderThumbnail(true)

  private renderUnloadedThumbnail = () => this.renderThumbnail(false)

  private renderThumbnail = (loaded: boolean) => (
    <Thumbnail
      // adds sandboxing to a strict level
      sandbox=""
      style={{ opacity: loaded ? 1 : 0 }}
      frameBorder="0"
      {...this.props}
      onLoad={this.onLoad}
    />
  )

  private handleVisibilityChange = (visible: boolean) => {
    if (!visible) {
      this.setState({ loaded: false })
    }
  }

  private onLoad = () => {
    this.setState({ loaded: true })
  }
}

const Thumbnail = styled.iframe`
  width: 860px;
  height: 1100px;
  zoom: 0.75;
  transform: scale(0.5);
  transform-origin: 0 0;

  /* display thumbnail with transition when loaded */
  opacity: 0;
  transition: all 150ms ease-in-out;
`

const WarningSpan = styled.span`
  position: relative;
  top: 2px;
  color: ${Colors.coral};
`

const MessageWrapper = styled.div`
  margin-bottom: 32px;
`

const WidthLimit = styled.div`
  max-width: 360px;
`

const MessageElem = styled.div`
  ${(props: { isInbound?: boolean }) => css`
    display: flex;
    justify-content: ${props.isInbound ? 'flex-start' : 'flex-end'};
    position: relative;
  `};
`

const MessageBubble = styled.div`
  ${(props: { isInbound?: boolean; hasMedia?: boolean; isSelected?: boolean }) => css`
    background-color: ${props.isInbound ? Colors.steel : Colors.charcoal};
    border-radius: 4px;
    box-sizing: border-box;
    padding: ${props.hasMedia ? '0' : '16px 16px 12px 16px'};
    position: relative;
    border-width: 1px;
    border-style: solid;
    border-color: ${props.isSelected ? Colors.sky : 'transparent'};
    cursor: pointer;
    &:hover {
      border-color: ${props.isSelected ? Colors.sky : DarkColors.sky};
    }
  `};
`

const MessageTimestamp = styled.div`
  ${(props: { isInbound?: boolean; highlight?: boolean }) => css`
    cursor: default;
    bottom: 100%;
    opacity: ${props.highlight ? 1 : 0.4};
    position: absolute;
    left: ${props.isInbound ? '-1px' : 'auto'};
    right: ${props.isInbound ? 'auto' : '0'};
    transition: opacity 0.1s;
    white-space: nowrap;
    ${MessageBubble}:hover & {
      opacity: 1;
    }
  `};
`

const MessageSubject = styled(BodyText)`
  color: ${Colors.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-bottom: 8px;
  border-bottom: 1px solid ${Colors.midway};
  margin-bottom: 24px;
`

const MessageBody = styled(BodyText)`
  ${(props: { isSelected?: boolean }) => css`
    color: ${Colors.white};
    position: relative;
    max-height: ${props.isSelected ? 'none' : '300px'};
    overflow: hidden;
  `}
`

const Ink = ColorsRGB.ink

const MsgExpandButton = styled(BodyText)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background-image: linear-gradient(rgba(${Ink.r}, ${Ink.g}, ${Ink.b}, 0), rgba(${Ink.r}, ${Ink.g}, ${Ink.b}, 1));
  text-align: center;
`

const MessageImageContainer = styled.div`
  overflow: hidden;
  border-radius: 4px;
`

const MessageImage = styled.img`
  max-width: 100%;
  vertical-align: top;
`

class Call extends React.PureComponent<
  { msg: FetchThreadQuery['thread']['messagesWithLinks']['messages'][0] },
  { now: string }
> {
  private onClockBound?: ClockListener

  constructor(props: { msg: Message }) {
    super(props)
    this.state = {
      now: clock.now(),
    }
  }

  componentDidMount() {
    const { msg } = this.props
    if (msg.callStatus === VoiceCallStatus.IN_PROGRESS) {
      this.onClockBound = str => this.onClock(str)
      clock.addListener(this.onClockBound)
    }
  }

  componentWillUnmount() {
    if (this.onClockBound) {
      clock.removeListener(this.onClockBound)
    }
  }

  render() {
    const { now } = this.state
    const { msg } = this.props
    if (msg.callStatus === VoiceCallStatus.MISSED) {
      return <CallSpan text="missed call" />
    } else if (msg.callStatus === VoiceCallStatus.ROUTED) {
      return <CallSpan text="re-routed call" duration={msg.callDuration} connected />
    } else if (msg.callStatus === VoiceCallStatus.AUTOMATED) {
      // TODO: automated call type
      return <CallSpan text="called prospects back" duration={msg.callDuration} connected />
    } else if (msg.callStatus === VoiceCallStatus.IN_PROGRESS) {
      return <CallSpan text="call in progress" callStart={msg.callStart} now={now} connected />
    } else if (msg.callStatus === VoiceCallStatus.ANSWERED) {
      return <CallSpan text="outbound call" duration={msg.callDuration} connected />
    } else if (msg.callStatus === VoiceCallStatus.BUSY) {
      return <CallSpan text="busy line" duration={msg.callDuration} />
    } else if (msg.callStatus === VoiceCallStatus.FAILED) {
      return <CallSpan text="call failed" duration={msg.callDuration} />
    } else if (msg.callStatus === VoiceCallStatus.NO_ANSWER) {
      return <CallSpan text="no answer" duration={msg.callDuration} />
    } else {
      return <CallSpan text="missed call" />
    }
  }

  private onClock(now: string) {
    this.setState({ now })
  }
}

interface CallSpanProps {
  text: string
  connected?: boolean
  duration?: number | null
  callStart?: string | null
  now?: string | null
}

function CallSpan({ text, connected, duration, callStart, now }: CallSpanProps) {
  let dur
  if (duration) {
    dur = duration
  } else if (now && callStart) {
    dur = Math.floor((new Date(now).getTime() - new Date(callStart).getTime()) / 1000)
  }

  let durFmt
  if (dur != null) {
    durFmt = Math.floor(dur / 60) + ':' + ('' + ((dur % 60) + 100)).substring(1)
  }

  return (
    <ColoredSpan color={connected ? 'mint' : 'coral'}>
      <Icon name={connected ? 'message-call-connected' : 'message-call-missed'} />
      <Spacer width={8} />
      {text} {durFmt}
    </ColoredSpan>
  )
}

const CharLogo = styled.span`
  display: inline-block;
  transform: scale(1.6);
  text-align: center;
  width: 14px;
  margin-right: 8px;
`

const ReplyToContainer = styled(BodyText)`
  float: right;
`
